import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import type { EventInteractionArgs } from 'react-big-calendar/lib/addons/dragAndDrop';
import type { SlotInfo } from 'react-big-calendar';
import { datetime } from 'rrule';
import { RobotCalendarEvent, RobotScheduleEvent } from '../components/RobotSchedule/RobotSchedule.types';
import {
  HandleOpenCreateModalOptions,
  MachineDetailsRobotSchedulePeriod,
} from '../types/MachineDetailsRobotSchedule.types';
import { toOneTimeRRuleSet } from '../utils/rruleDateTime';
import { isScheduleDateTimeInThePast, mapOccurrenceToScheduleEvent } from '../utils/schedule';
import type { Machine } from 'app/modules/machine-inventory/interfaces/Machine.types';
import { toast } from 'app/cross-cutting-concerns/toasts/kcc/toast';
import { RobotScheduleActions } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotSchedule/robotScheduleSlice';
import * as robotScheduleSelectors from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotSchedule/robotScheduleSelectors';

// react-big-calendar renders events that cross midnight in the all-day row, which is hidden
// via CSS, so split such events into one time-grid segment per day instead.
const toCalendarEvents = (scheduleEvent: RobotScheduleEvent): RobotCalendarEvent[] => {
  const end = dayjs(scheduleEvent.endAt).add(scheduleEvent.chargingDurationMs, 'ms');
  const segments: RobotCalendarEvent[] = [];
  let segmentStart = dayjs(scheduleEvent.startAt);

  while (segmentStart.isBefore(end)) {
    const endOfDay = segmentStart.endOf('day');
    const segmentEnd = end.isAfter(endOfDay) ? endOfDay : end;

    segments.push({
      title: scheduleEvent.name,
      start: segmentStart.toDate(),
      end: segmentEnd.toDate(),
      resource: { scheduleEvent, isContinuation: segments.length > 0 },
    });

    segmentStart = endOfDay.add(1, 'ms');
  }

  return segments;
};

interface UseRobotScheduleEventsReturn {
  events: RobotCalendarEvent[];
  isLoading: boolean;
  handleEventDrop: (args: EventInteractionArgs<RobotCalendarEvent>) => void;
  handleSelectSlot: (slotInfo: SlotInfo) => void;
  handleSelectEvent: (event: RobotCalendarEvent) => void;
}

export const useRobotScheduleEvents = ({
  period,
  handleOpenCreateModal,
  handleOpenEditModal,
  isB50,
  robot,
  isRobotVersionSupported,
}: {
  period: MachineDetailsRobotSchedulePeriod;
  handleOpenCreateModal: (options?: HandleOpenCreateModalOptions) => void;
  handleOpenEditModal: (event: RobotCalendarEvent) => void;
  isB50: boolean;
  robot: Machine;
  isRobotVersionSupported: boolean;
}): UseRobotScheduleEventsReturn => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const periodStartKey = period.startDate.toISOString();
  const periodEndKey = period.endDate.toISOString();

  const occurrences = useSelector(robotScheduleSelectors.selectRobotScheduleOccurrences);
  const isLoadingOccurrences = useSelector(robotScheduleSelectors.selectRobotScheduleOccurrencesIsLoading);
  const isUpdating = useSelector(robotScheduleSelectors.selectUpdateScheduleIsLoading);
  const error = useSelector(robotScheduleSelectors.selectRobotScheduleOccurrencesError);

  useEffect(() => {
    if (!isRobotVersionSupported) {
      return;
    }

    dispatch(
      RobotScheduleActions.robotScheduleListOccurrencesRequest({
        materialNumber: robot.materialNumber,
        serialNumber: robot.serialNumber,
        period: {
          startAt: periodStartKey,
          endAt: periodEndKey,
        },
      })
    );
  }, [dispatch, robot.materialNumber, robot.serialNumber, periodStartKey, periodEndKey, isRobotVersionSupported]);

  // Keeps the calendar in sync with schedules created, changed or deleted elsewhere (e.g. on the robot).
  useEffect(() => {
    if (!isRobotVersionSupported) {
      return undefined;
    }

    dispatch(
      RobotScheduleActions.followScheduleChanges({
        materialNumber: robot.materialNumber,
        serialNumber: robot.serialNumber,
      })
    );

    return (): void => {
      dispatch(RobotScheduleActions.stopFollowingScheduleChanges());
    };
  }, [dispatch, robot.materialNumber, robot.serialNumber, isRobotVersionSupported]);

  useEffect(() => {
    if (!error) {
      return;
    }

    toast.error({
      message: t('machineDetails.robotSchedule.loadOccurrencesError'),
      className: 'kcc-toast--message-regular',
    });
  }, [error, t]);

  const events = useMemo(
    () =>
      (occurrences ?? [])
        .map(occurrence => mapOccurrenceToScheduleEvent({ occurrence, isB50 }))
        .flatMap(toCalendarEvents),
    [occurrences, isB50]
  );

  const handleEventDrop = useCallback(
    (args: EventInteractionArgs<RobotCalendarEvent>) => {
      if (!isB50) {
        toast.error({
          message: t('machineDetails.robotSchedule.dragAndDropNotSupported'),
          className: 'kcc-toast--message-regular',
        });
        return;
      }

      const { event, start: updatedStart } = args;

      if (isScheduleDateTimeInThePast(event.start) || isScheduleDateTimeInThePast(updatedStart)) {
        toast.error({
          message: t('machineDetails.robotSchedule.updateForbiddenInThePast'),
          className: 'kcc-toast--message-regular',
        });
        return;
      }

      const scheduleEvent = event.resource.scheduleEvent;
      const occurrence = scheduleEvent.occurrence;
      const updatedOccurrenceStart = dayjs(updatedStart).second(0).millisecond(0);

      const updatedDtStart = datetime(
        updatedOccurrenceStart.year(),
        updatedOccurrenceStart.month() + 1, // rrule uses 1-12 for months
        updatedOccurrenceStart.date(),
        updatedOccurrenceStart.hour(),
        updatedOccurrenceStart.minute()
      );

      dispatch(
        RobotScheduleActions.updateScheduleRequest({
          id: occurrence.scheduleId,
          name: scheduleEvent.name,
          occurrence: occurrence.startDate,
          routeId: occurrence.route?.id,
          routineId: occurrence.routine?.id,
          rrule: toOneTimeRRuleSet({ dtstart: updatedDtStart }).toString(),
          applyToFollowing: false,
        })
      );
    },
    [isB50, dispatch, t]
  );

  const handleSelectSlot = useCallback(
    (slotInfo: SlotInfo) => {
      // Only past slots are rejected; a CV50 slot on another weekday is allowed.
      if (isScheduleDateTimeInThePast(slotInfo.start)) {
        toast.error({
          message: t('machineDetails.robotSchedule.createForbiddenInThePast'),
          className: 'kcc-toast--message-regular',
        });
        return;
      }

      const selectedStart = slotInfo.start.getTime();
      const selectedEnd = slotInfo.end.getTime();
      const hasEventOverlap = events.some(
        event => selectedStart < event.end.getTime() && selectedEnd > event.start.getTime()
      );

      handleOpenCreateModal({ dateTime: slotInfo.start, hasEventOverlap });
    },
    [handleOpenCreateModal, events, t]
  );

  const handleSelectEvent = useCallback(
    (event: RobotCalendarEvent) => {
      handleOpenEditModal(event);
    },
    [handleOpenEditModal]
  );

  return {
    events,
    isLoading: isLoadingOccurrences || isUpdating,
    handleEventDrop,
    handleSelectSlot,
    handleSelectEvent,
  };
};
